/**
 * Tool UI for generate_image — compact card showing generation status
 * and the workspace path where each image was saved.
 */
import { makeAssistantToolUI } from '@assistant-ui/react';
import { ImageIcon, XCircle } from 'lucide-react';
import { ToolRow, plainText, DELIVERABLE_SURFACE } from '../toolRow';

interface ImageGenArgs {
  /** Only on calls from before generate_image had one model. */
  model?: string;
  prompt?: string;
  images?: Array<{ prompt: string; saveToWorkspace: string }>;
  sourceImages?: Array<{ type: 'url' | 'workspace'; value: string }>;
  aspectRatio?: string;
  resolution?: string;
  transparent?: boolean;
  saveToWorkspace?: string;
  _thought?: string;
}

interface ImageGenResult {
  model?: string;
  mimeType?: string;
  sizeBytes?: number;
  aspectRatio?: string | number;
  resolution?: string;
  textResponse?: string;
  savedPath?: string;
  images?: Array<{ savedPath?: string; saveToWorkspace?: string; error?: string }>;
  rendered?: number;
  failed?: number;
  durationMs?: number;
  error?: string;
}

const MODEL_LABELS: Record<string, string> = {
  'gpt-image-2.5-sunburst': 'GPT Image 2.5 Sunburst',
  'gemini-3.1-flash-image-preview': 'Gemini 3.1 Flash',
  'gemini-3-pro-image-preview': 'Gemini 3 Pro',
  'grok-imagine-image-pro': 'Grok',
};

export const ImageGenToolUI = makeAssistantToolUI<ImageGenArgs, ImageGenResult>({
  toolName: 'generate_image',
  render: function ImageGenRender({ args, result, status }) {
    const isRunning = status?.type === 'running';
    const failed = !!result?.error;
    const batch = Array.isArray(result?.images);
    const saved = !!result?.savedPath || (batch && (result!.rendered ?? 0) > 0);

    const model = result?.model ?? args.model ?? 'gpt-image-2.5-sunburst';
    const modelLabel = MODEL_LABELS[model] ?? model;
    const what = args.transparent ? 'transparent asset' : 'image';
    const n = args.images?.length;
    const label = n
      ? `Generating ${n} ${what}${n === 1 ? '' : 's'}`
      : `Generating ${what}: ${args.prompt?.slice(0, 40) ?? '…'}`;
    const thought = plainText(args._thought);

    if (isRunning || (!saved && !failed && !batch)) {
      return <ToolRow icon={<ImageIcon />} label={thought || label} running={isRunning} open={false} />;
    }

    if (failed || !saved) {
      return (
        <ToolRow
          icon={<XCircle className="text-red-500" />}
          label={label}
          meta={<span className="text-red-600">Failed</span>}
          tone="error"
          open={false}
        />
      );
    }

    const meta = [
      !batch && result!.sizeBytes != null ? `${(result!.sizeBytes / 1024).toFixed(0)} KB` : null,
      batch && result!.failed ? `${result!.failed} failed` : null,
      `${args.aspectRatio ?? '1:1'} · ${result!.resolution ?? args.resolution ?? '1K'}`,
      modelLabel,
      result!.durationMs ? `${(result!.durationMs / 1000).toFixed(1)}s` : null,
    ].filter(Boolean).join(' · ');

    return (
      <div className={`${DELIVERABLE_SURFACE} flex items-center gap-2.5 px-3 py-2`}>
        <ImageIcon className="w-3.5 h-3.5 shrink-0 text-zinc-400" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-meta text-zinc-900">
            {batch ? `${result!.rendered} ${what}${result!.rendered === 1 ? '' : 's'} saved` : result!.savedPath}
          </div>
          <div className="truncate text-xs text-zinc-400">{meta}</div>
        </div>
      </div>
    );
  },
});
